import type { Client } from '../../../api/clients';
import type { SalesPerson } from '../../../api/clients';
import type { OrderFormLine, OrderFormTaxConfig, OrderTotals } from './types';
import { normalizeDiscountPercent } from './orderFormTotals';
import { resolveOrderLineTaxForPersistence } from './orderFormTax';

export type OrderFormSaveValidationResult =
  | { valid: true }
  | { valid: false; message?: string };

export function validateOrderFormForSave(input: {
  selectedClient: Client | null;
  selectedProducts: OrderFormLine[];
  selectedVendorId: string;
}): OrderFormSaveValidationResult {
  if (!input.selectedClient || input.selectedProducts.length === 0) {
    return { valid: false };
  }
  if (!input.selectedVendorId) {
    return { valid: false };
  }
  const invalidQuantity = input.selectedProducts.some(p => {
    const q = Number(p.selectedQuantity);
    return !Number.isFinite(q) || q <= 0;
  });
  if (invalidQuantity) {
    return {
      valid: false,
      message: 'Cada producto debe tener una cantidad mayor que cero.',
    };
  }
  return { valid: true };
}

export function normalizeCreditFields(input: {
  creditEnabled: boolean;
  creditTermsDays: string;
  creditDueDate: string;
}): { creditTermsDays: number | null; dueDate: Date | null } {
  if (!input.creditEnabled) {
    return { creditTermsDays: null, dueDate: null };
  }
  const parsedCreditTerms = Number(input.creditTermsDays);
  const creditTermsPositive =
    input.creditTermsDays.trim() !== '' &&
    Number.isFinite(parsedCreditTerms) &&
    Math.round(parsedCreditTerms) > 0;
  const normalizedCreditTerms = creditTermsPositive
    ? Math.round(parsedCreditTerms)
    : null;
  const dueRaw = input.creditDueDate.trim();
  const normalizedDueDate = dueRaw
    ? new Date(`${dueRaw}T00:00:00`)
    : null;
  return {
    creditTermsDays: normalizedCreditTerms,
    dueDate: normalizedDueDate,
  };
}

export function resolveCreatorSalesPersonString(input: {
  selectedVendorId: string;
  salesPeople: SalesPerson[];
  currentUserEmail?: string | null;
}): string {
  if (input.selectedVendorId === 'admin') {
    return input.currentUserEmail?.trim() || 'Administrador';
  }
  const sp = input.salesPeople.find(s => s.id === input.selectedVendorId);
  if (!sp) return input.selectedVendorId;
  return (
    sp.email?.trim() ||
    sp.code?.trim() ||
    sp.name?.trim() ||
    sp.id
  );
}

export type OrderProductPayloadLine = {
  productId: string;
  code: string;
  name: string;
  brand?: string;
  description?: string;
  price: number;
  quantity: number;
  discount: number;
  cost?: number;
  imageUrl?: string;
  taxId?: string;
  taxRate: number;
  warehouseId?: string;
  profitPlus2k8?: {
    fields: { co_sub: string; co_alma: string };
    syncedAt: null;
  };
};

export function buildOrderProductsPayload(
  lines: OrderFormLine[],
  options: {
    applyIva: boolean;
    taxConfig: OrderFormTaxConfig;
    showSaleProfitPlusFields: boolean;
    defaultWarehouseId?: string;
  }
): OrderProductPayloadLine[] {
  return lines.map(p => {
    const price =
      p.customPrice !== undefined ? p.customPrice : Number(p.price) || 0;
    const { taxId, taxRate } = resolveOrderLineTaxForPersistence(
      p,
      options.taxConfig,
      options.applyIva
    );
    const selectedCoSub = String(p.selectedCoSub ?? '').trim();
    const productId = String(p.productId || p.id || '').trim();
    const cost = Number(p.cost);
    const lineWarehouseId = String(p.warehouseId || options.defaultWarehouseId || '').trim();
    return {
      productId,
      code: p.code ?? '',
      name: p.name ?? '',
      brand: p.brand,
      description: p.description,
      price,
      quantity: Number(p.selectedQuantity) || 0,
      discount: normalizeDiscountPercent(p.discount),
      ...(Number.isFinite(cost) && cost > 0 ? { cost } : {}),
      imageUrl: p.imageUrl,
      taxId,
      taxRate,
      ...(lineWarehouseId ? { warehouseId: lineWarehouseId } : {}),
      profitPlus2k8:
        options.showSaleProfitPlusFields && selectedCoSub
          ? {
              fields: {
                co_sub: selectedCoSub,
                co_alma: selectedCoSub,
              },
              syncedAt: null,
            }
          : undefined,
    };
  });
}

export function buildOrderCommonFields(input: {
  selectedClient: Client;
  deliveryAddress: string;
  creatorSalesPerson: string;
  paymentMethod: string;
  paymentCurrency: string;
  currency: string;
  creditEnabled: boolean;
  creditTermsDays: string;
  creditDueDate: string;
  totals: OrderTotals;
  exchangeRate: number;
  comment: string;
  products: OrderProductPayloadLine[];
  warehouseId?: string;
  orderDiscountPercent?: number | null;
  saleProfitIntegration?: unknown;
  /** Business/emission date (create only). ISO date YYYY-MM-DD or empty → omit. */
  emissionDate?: string;
}) {
  const credit = normalizeCreditFields({
    creditEnabled: input.creditEnabled,
    creditTermsDays: input.creditTermsDays,
    creditDueDate: input.creditDueDate,
  });
  const orderDiscountPercent = normalizeDiscountPercent(
    input.orderDiscountPercent
  );
  const emissionCreated = resolveEmissionCreated(input.emissionDate);

  return {
    clientId: input.selectedClient.id,
    clientName:
      input.selectedClient.businessName ||
      input.selectedClient.legalName ||
      input.selectedClient.rif,
    deliveryAddress: input.deliveryAddress.trim(),
    creatorSalesPerson: input.creatorSalesPerson,
    paymentMethod: input.paymentMethod,
    paymentCurrency: input.paymentCurrency || input.currency,
    creditTermsDays: credit.creditTermsDays,
    dueDate: credit.dueDate,
    subtotal: input.totals.subtotal,
    ivaAmount: input.totals.ivaAmount > 0 ? input.totals.ivaAmount : undefined,
    total: input.totals.total,
    exchangeRate: input.exchangeRate,
    comment: input.comment || '',
    products: input.products,
    orderDiscountPercent,
    ...(input.warehouseId ? { warehouseId: input.warehouseId } : {}),
    ...(input.saleProfitIntegration
      ? { profitPlus2k8: input.saleProfitIntegration }
      : {}),
    ...(emissionCreated ? { created: emissionCreated } : {}),
  };
}

/**
 * Business/emission date for create (ADR-0002).
 * - Empty → omit (server uses wall-clock now).
 * - Today (local calendar) → omit so admin matches app wall-clock time
 *   instead of clustering at local noon.
 * - Other dates → local noon (stable calendar day across UTC offsets).
 */
export function resolveEmissionCreated(
  emissionDate: string | undefined
): Date | undefined {
  const emissionRaw = (emissionDate || '').trim();
  if (!emissionRaw || !/^\d{4}-\d{2}-\d{2}$/.test(emissionRaw)) {
    return undefined;
  }

  const now = new Date();
  const todayStr = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  if (emissionRaw === todayStr) {
    return undefined;
  }

  const emissionCreated = new Date(`${emissionRaw}T12:00:00`);
  return Number.isNaN(emissionCreated.getTime()) ? undefined : emissionCreated;
}
